/**
 * Textos de la opción "aplicar este concepto a los empleados que ya existen", compartidos por
 * las dos pantallas del concepto (Agregar y Editar) para que digan exactamente lo mismo.
 *
 * Contexto: `automaticoGlobal` sólo lo lee el ALTA de empleado, así que un concepto creado
 * después de dar de alta a la gente no le sale a nadie. Esta opción es el único camino para
 * alcanzarlos.
 *
 * Los OMITIDOS se reportan siempre: son el caso que vuelve inexplicable el resultado si se
 * calla ("le di aplicar a todos y a tres no les llegó"). En las dos direcciones el motivo es el
 * mismo campo con significado doble — `Empleado.conceptosIds` vacío quiere decir "le aplican
 * TODOS los conceptos" — así que tocarlos habría hecho lo contrario de lo que se pidió.
 */

const plural = (n) => (n === 1 ? "empleado" : "empleados");

/**
 * Etiqueta del checkbox. Se completa con el conteo real en cuanto el backend responde.
 *
 * @param {boolean} automaticoGlobal - decide la dirección: prendido agrega, apagado quita.
 * @param {object|null} conteo - respuesta de conteoEmpleadosParaConcepto.
 * @param {boolean} consultando
 */
export const etiquetaAplicarAEmpleados = (automaticoGlobal, conteo, consultando) => {
  const verbo = automaticoGlobal ? "Aplicar también a" : "Quitar de";
  const cola = automaticoGlobal ? "que ya existen" : "que lo tienen";

  if (consultando) return `${verbo} los empleados ${cola} (consultando…)`;
  if (!conteo) return `${verbo} los empleados ${cola}`;

  const n = conteo.afectables || 0;
  if (n === 0) {
    return automaticoGlobal
      ? "Ningún empleado necesita este cambio"
      : "Ningún empleado tiene este concepto asignado";
  }
  return `${verbo} los ${n} ${plural(n)} ${cola}`;
};

/**
 * Aviso bajo el checkbox para los empleados que la operación NO va a tocar, con el motivo.
 * Devuelve cadena vacía cuando no hay nada que advertir.
 */
export const avisoOmitidosAplicar = (automaticoGlobal, conteo) => {
  const omitidos = conteo?.omitidos || 0;
  if (!omitidos) return "";

  return automaticoGlobal
    ? `${omitidos} ${plural(omitidos)} no se tocarán: no tienen conceptos asignados, así que ya reciben todos.`
    : `${omitidos} ${plural(omitidos)} no se tocarán: es su único concepto asignado y quitárselo los dejaría recibiendo todos.`;
};

/**
 * Texto del resultado, para el mensaje que sigue al guardado.
 *
 * @param {object} resultado - `resultadoEmpleados` de la respuesta del backend.
 * @returns {string} vacío si no hubo operación sobre empleados.
 */
export const textoResultadoEmpleados = (resultado) => {
  if (!resultado) return "";

  const { accion, afectados = 0, omitidos = 0 } = resultado;
  const partes = [
    accion === "quitar"
      ? `Se quitó de ${afectados} ${plural(afectados)}`
      : `Se agregó a ${afectados} ${plural(afectados)}`,
  ];

  if (omitidos > 0) {
    partes.push(
      accion === "quitar"
        ? `${omitidos} se omitieron porque era su único concepto asignado: quitárselo los dejaría recibiendo todos`
        : `${omitidos} no se tocaron porque no tienen conceptos asignados y ya los reciben todos`
    );
  }

  return partes.join(". ") + ".";
};
